/**
 * Parses React component stack into structured tree
 * Works with ErrorBoundary's componentStack format
 */

export interface ComponentNode {
  name: string
  file?: string
  line?: number
  column?: number
}

export interface ParsedComponentTree {
  componentTree: ComponentNode[]
  rawComponentStack: string
}

/**
 * Parse React component stack string into structured tree
 * 
 * Example input:
 * "at StoryDetailClient (story-detail-client.tsx:145:12)
 *  at StoryDirection (story-detail-client.tsx:1087:5)
 *  at StoryFoundationSection (StoryFoundationSection.tsx:23:10)"
 */
export function parseComponentStack(
  componentStack?: string
): ParsedComponentTree | null {
  if (!componentStack || typeof componentStack !== 'string') {
    return null
  }

  const lines = componentStack.trim().split('\n')
  const componentTree: ComponentNode[] = []

  // Regex to match: "at ComponentName (file.tsx:line:col)" or "at ComponentName"
  const componentRegex = /at\s+(\w+)(?:\s+\(([^:]+):(\d+):(\d+)\))?/g

  for (const line of lines) {
    const match = componentRegex.exec(line.trim())
    if (match) {
      const [, name, file, lineStr, colStr] = match
      if (name) {
        componentTree.push({
          name,
          ...(file && { file }),
          ...(lineStr && { line: parseInt(lineStr, 10) }),
          ...(colStr && { column: parseInt(colStr, 10) })
        })
      }
    }
  }

  return {
    componentTree,
    rawComponentStack: componentStack
  }
}

